import {
	ACTIVITY_WINDOW_DAYS,
	COVER_PLACEHOLDER_SRC,
} from '../../../shared/config/constants'
import {
	ACTIVITY_TYPES,
	type ActivityFeedItem,
	type ActivityType,
	type FriendshipSummary,
	type FriendsResponse,
	type SocialUserSummary,
} from '../../../shared/types/social'
import { infoTooltipTemplate } from '../../components/infoTooltipTemplates'
import { renderMultiSelect } from '../../components/inputs/multiSelectTemplates'
import { releaseHeaderRatingTemplate } from '../../components/release/releaseRatingTemplates'
import { formatRelativeTime } from '../../utils/format'
import { escapeHtml } from '../../utils/html'

const ACTIVITY_LABELS: Record<ActivityType, string> = {
	listened: 'Listened',
	liked: 'Liked',
	rated: 'Rated',
	added: 'Added',
}

export function activityPageTemplate(types: ActivityType[]): string {
	return `
		<section id="activity-section">
			<header class="page-header activity-title-row">
				<h1>Activity</h1>
				${infoTooltipTemplate('activity-info-text', 'About Activity', `Your activity has no age limit. Friends’ activity covers the last ${ACTIVITY_WINDOW_DAYS} days.`)}
			</header>
			<div class="activity-toolbar">
				<div id="activity-type-filter" class="filter-container">
					${activityTypeFilterTemplate(types)}
				</div>
				<div class="activity-actions">
					${activityScopeButtonTemplate(false)}
					<button type="button" class="btn outlined icon filter-icon-button" data-manage-friends data-friend-request-indicator="Manage friends" aria-label="Manage friends" title="Manage friends">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user-plus" aria-hidden="true">
							<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
							<circle cx="9" cy="7" r="4"/>
							<line x1="19" x2="19" y1="8" y2="14"/>
							<line x1="22" x2="16" y1="11" y2="11"/>
						</svg>
					</button>
				</div>
			</div>
			<div class="activity-friends-note" data-no-friends hidden>
				<p class="f secondary">Add friends to see their activity here.</p>
				<button type="button" class="btn noFrame icon small" data-dismiss-friends-note aria-label="Dismiss friends suggestion">
					<svg aria-hidden="true"><use href="#pepicons-pop-times"></use></svg>
				</button>
			</div>
			<div class="activity-feed">
				<ol class="activity-list" data-activity-list></ol>
				<div class="activity-status" data-activity-status aria-live="polite"></div>
			</div>
		</section>
	`
}

/** Shows the current feed scope; the accessible name describes the next action. */
export function activityScopeButtonTemplate(onlyMine: boolean): string {
	const action = onlyMine ? 'Show all activity' : 'Show only my activity'
	const icon = onlyMine
		? '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>'
		: '<path d="M17 21v-1a2 2 0 00-2-2H9a2 2 0 00-2 2v1"/><path d="M19 10h1a2 2 0 012 2v1"/><path d="M5 10H4a2 2 0 00-2 2v1"/><circle cx="12" cy="11" r="3"/><circle cx="18" cy="4" r="2"/><circle cx="6" cy="4" r="2"/>'
	return `<button type="button" class="btn outlined icon filter-icon-button" data-activity-scope aria-pressed="${onlyMine}" aria-label="${action}" title="${action}"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icon}</svg></button>`
}

function activityTypeFilterTemplate(types: ActivityType[]): string {
	return renderMultiSelect({
		selectAll: true,
		allLabel: 'All activity',
		allSelected: ACTIVITY_TYPES.every((type) => types.includes(type)),
		label: 'Type',
		options: (Object.keys(ACTIVITY_LABELS) as ActivityType[]).map(
			(value) => ({
				value,
				label: ACTIVITY_LABELS[value],
			}),
		),
		selectId: 'activity-type-filter-control',
		selected: types,
	})
}

export function activityLoadingTemplate(): string {
	return `<div class="activity-loading" role="status" aria-label="Loading"><span class="spinner" aria-hidden="true"></span></div>`
}

export function activityErrorTemplate(): string {
	return `<div class="activity-empty"><p>Activity could not be loaded.</p><button type="button" class="btn secondary small" data-activity-retry>Try again</button></div>`
}

export function activityEmptyTemplate(filtered: boolean): string {
	return `<div class="activity-empty"><p>${
		filtered ? 'No activity matches these filters.' : 'No activity yet.'
	}</p>${filtered ? '<p class="f secondary">Choose another type to widen the feed.</p>' : ''}</div>`
}

export function activityItemTemplate(item: ActivityFeedItem): string {
	const actions: Record<ActivityType, string> = {
		listened: 'listened to',
		liked: 'liked',
		rated: `rated ${releaseHeaderRatingTemplate(item.rating ?? null, false)}`,
		added: 'added to their collection',
	}
	const artists = item.release.artists.join(', ')
	const cover = item.release.coverImage || COVER_PLACEHOLDER_SRC
	return `
		<li class="activity-item" data-activity-id="${escapeHtml(item.id)}">
			${avatarTemplate(item.actor)}
			<div class="activity-item-body">
				<div class="activity-copy">
					<p><strong>${escapeHtml(item.actor.username)}</strong> ${actions[item.type]}</p>
					<time class="activity-time" datetime="${escapeHtml(item.occurredAt)}">${escapeHtml(formatRelativeTime(item.occurredAt))}</time>
				</div>
				<button type="button" class="activity-release" data-release-id="${item.release.id}" aria-label="Open ${escapeHtml(item.release.title)}">
					<img src="${escapeHtml(cover)}" alt="" loading="lazy" onerror="this.src='${COVER_PLACEHOLDER_SRC}'" />
					<span>
						<strong>${escapeHtml(item.release.title)}</strong>
						<small>${escapeHtml(artists || 'Unknown artist')}${item.release.year ? ` · ${item.release.year}` : ''}</small>
					</span>
				</button>
			</div>
		</li>
	`
}

function avatarTemplate(user: SocialUserSummary): string {
	const profileUrl = `https://www.discogs.com/user/${encodeURIComponent(user.username)}`
	return `<a class="activity-avatar activity-avatar--initials" href="${escapeHtml(profileUrl)}" target="_blank" rel="noopener noreferrer" aria-label="View ${escapeHtml(user.username)} on Discogs"><span aria-hidden="true">${escapeHtml(user.initials)}</span>${user.avatarUrl ? `<img src="${escapeHtml(user.avatarUrl)}" alt="" loading="lazy" onerror="this.remove()" />` : ''}</a>`
}

export function friendsOverlayLoadingTemplate(): string {
	return `<section class="friends-overlay"><h1>Friends</h1>${activityLoadingTemplate()}</section>`
}

export function friendsOverlayTemplate(
	data: FriendsResponse,
	error = '',
): string {
	return `
		<section class="friends-overlay">
			<h1>Friends</h1>
			<p class="f secondary">Accepted friends can see each other’s listens, likes, ratings, and collection additions from the last ${ACTIVITY_WINDOW_DAYS} days, including before you became friends. Your own activity has no age limit. You can remove a friend at any time.</p>
			<form class="friend-add-form" data-friend-add-form>
				<div>
					<input id="friend-username" name="username" autocomplete="off" required placeholder="Discogs username" />
					<button type="submit" class="btn primary"><svg class="add-icon" aria-hidden="true"><use href="#symbols-plus"></use></svg>Add</button>
				</div>
				<p class="friend-form-error" data-friend-error${error ? '' : ' hidden'}>${escapeHtml(error)}</p>
			</form>
			<div data-friend-lists>${friendsListsTemplate(data)}</div>
		</section>
	`
}

export function friendsListsTemplate(data: FriendsResponse): string {
	const hasRelations =
		data.friends.length + data.incoming.length + data.outgoing.length > 0
	return `
			${data.incoming.length ? friendGroupTemplate('Incoming requests', data.incoming) : ''}
			${data.outgoing.length ? friendGroupTemplate('Sent requests', data.outgoing) : ''}
			${data.friends.length ? friendGroupTemplate('Friends', data.friends) : ''}
			${hasRelations ? '' : '<div class="activity-empty"><p>No friends or pending requests yet.</p><p class="f secondary">Both people must confirm before activity is shared.</p></div>'}
	`
}

export function friendsOverlayErrorTemplate(): string {
	return `<section class="friends-overlay"><h1>Friends</h1><div class="activity-empty"><p>Friends could not be loaded.</p><button type="button" class="btn secondary small" data-friends-retry>Try again</button></div></section>`
}

function friendGroupTemplate(
	title: string,
	items: FriendshipSummary[],
): string {
	return `<section class="friend-group"><h2>${title}</h2><ul>${items.map(friendRowTemplate).join('')}</ul></section>`
}

function friendRowTemplate(item: FriendshipSummary): string {
	const actions =
		item.state === 'incoming'
			? `<button type="button" class="btn primary small" data-friend-action="accept">Accept</button><button type="button" class="btn secondary small" data-friend-action="decline">Decline</button>`
			: item.state === 'outgoing'
				? `<button type="button" class="btn secondary small" data-friend-action="cancel">Cancel</button>`
				: `<button type="button" class="btn danger small" data-friend-action="remove">Remove</button>`
	return `
		<li class="friend-row" data-friendship-id="${escapeHtml(item.id)}" data-friend-username="${escapeHtml(item.user.username)}">
			${avatarTemplate(item.user)}
			<strong>${escapeHtml(item.user.username)}</strong>
			<div>${actions}</div>
		</li>
	`
}
